import { useEffect, useState } from 'react';
import { fill } from './chartLanguage';
import { LANGUAGE_ANSWER_OPTIONS } from './siteLanguages';
import english from './dailyCopy/en.json';

const VALID = new Set(
  LANGUAGE_ANSWER_OPTIONS.map((option) => option.value).filter(Boolean),
);

const COPY_LOADERS = {
  es: () => import('./dailyCopy/es.json'),
  hi: () => import('./dailyCopy/hi.json'),
  zh: () => import('./dailyCopy/zh.json'),
  ar: () => import('./dailyCopy/ar.json'),
  fr: () => import('./dailyCopy/fr.json'),
  pt: () => import('./dailyCopy/pt.json'),
  de: () => import('./dailyCopy/de.json'),
  ja: () => import('./dailyCopy/ja.json'),
  ko: () => import('./dailyCopy/ko.json'),
  it: () => import('./dailyCopy/it.json'),
  tr: () => import('./dailyCopy/tr.json'),
  id: () => import('./dailyCopy/id.json'),
  pl: () => import('./dailyCopy/pl.json'),
  vi: () => import('./dailyCopy/vi.json'),
  th: () => import('./dailyCopy/th.json'),
  nl: () => import('./dailyCopy/nl.json'),
  ru: () => import('./dailyCopy/ru.json'),
  fil: () => import('./dailyCopy/fil.json'),
  uk: () => import('./dailyCopy/uk.json'),
  ro: () => import('./dailyCopy/ro.json'),
  sv: () => import('./dailyCopy/sv.json'),
  cs: () => import('./dailyCopy/cs.json'),
  el: () => import('./dailyCopy/el.json'),
  hu: () => import('./dailyCopy/hu.json'),
};

const STATUS_KEYS = {
  'Exact today': 'exact',
  'Exact tomorrow': 'exactTomorrow',
  'Exact this week': 'exactWeek',
  Applying: 'applying',
  Separating: 'separating',
  'Sign change': 'signChange',
};

const NOTE_KEYS = {
  'Add a birth time to include your Moon, Ascendant, and houses.': 'time',
  'The time zone could not be read, so times are shown in UTC.': 'zone',
  'Houses could not be calculated for this birthplace, so houses are left out.': 'houses',
  'in orb; the exact time falls outside this week': 'farWeek',
  'in orb; exact date more than six months away': 'farSlow',
};

export function resolveDailyLanguage(search) {
  try {
    const query = new URLSearchParams(search || '').get('lang');
    if (query && VALID.has(query)) return query;
    const stored = localStorage.getItem('nowtarot_birth_chart_language');
    if (stored && VALID.has(stored)) return stored;
  } catch {
    // prerender or private mode
  }
  return 'en';
}

export function useDailyCopy(lang) {
  const [copy, setCopy] = useState(english);

  useEffect(() => {
    if (!lang || lang === 'en') {
      setCopy(english);
      return undefined;
    }
    const load = COPY_LOADERS[lang];
    if (!load) {
      setCopy(english);
      return undefined;
    }
    let live = true;
    load()
      .then((mod) => {
        if (live) setCopy(mod.default || mod);
      })
      .catch(() => {
        if (live) setCopy(english);
      });
    return () => {
      live = false;
    };
  }, [lang]);

  return copy;
}

export function bodyName(copy, name) {
  return copy.bodies?.[name] || name || '';
}

export function aspectName(copy, name) {
  return copy.aspects?.[name] || name || '';
}

export function signName(copy, name) {
  return copy.signs?.[name] || name || '';
}

export function phaseName(copy, sky) {
  if (!sky) return '';
  return copy.phases?.[sky.phase_key] || sky.phase || '';
}

export function statusName(copy, label) {
  const key = STATUS_KEYS[label];
  return (key && copy.status?.[key]) || label || '';
}

export function noteText(copy, note) {
  const key = NOTE_KEYS[note];
  return (key && copy.notes?.[key]) || note;
}

export function formatWhen(iso, language) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return new Intl.DateTimeFormat(language || 'en', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatDay(isoDate, language) {
  if (!isoDate) return '';
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(isoDate);
  return new Intl.DateTimeFormat(language || 'en', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function keywordLine(copy, row) {
  const natalKey = copy.planetKeywords?.[row.natal] ? row.natal : row.natal_label;
  const house = row.house
    ? fill(copy.keywordHouse, { house: copy.houseKeywords?.[String(row.house)] || row.house })
    : '';
  return fill(copy.keywordLine, {
    transit: bodyName(copy, row.transit),
    transitKeyword: copy.planetKeywords?.[row.transit] || row.transit,
    aspect: aspectName(copy, row.aspect),
    aspectKeyword: copy.aspectKeywords?.[row.aspect] || row.aspect,
    natal: bodyName(copy, row.natal_label || row.natal),
    natalKeyword: copy.planetKeywords?.[natalKey] || row.natal_label || row.natal,
    house,
  });
}
